import { useCallback, useEffect, useRef } from "react";
import type { MutableRefObject } from "react";

const DEFAULT_REORDER_SAVE_DELAY_MS = 500;

/**
 * Shared drag-and-drop reorder mechanics for Table's `handleRowReorder`/
 * `handleDrop` props: writes the latest dropped row order into `rowsRef` and
 * coalesces rapid successive drops (e.g. moving several rows back to back)
 * into a single debounced save, instead of one request per drop.
 *
 * Callers own `rowsRef` and the actual persistence (endpoint, success/error
 * handling, `isSavingReorder` state) via `persistReorder`, which should read
 * the final order from `rowsRef.current`.
 */
export function useDebouncedRowReorder<Row>(
  rowsRef: MutableRefObject<Row[]>,
  persistReorder: () => void | Promise<void>,
  delay: number = DEFAULT_REORDER_SAVE_DELAY_MS
) {
  const reorderSaveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleRowReorder = useCallback(
    (rows: Row[]) => {
      rowsRef.current = rows;
    },
    [rowsRef]
  );

  const handleDrop = useCallback(() => {
    clearTimeout(reorderSaveTimeoutRef.current);
    reorderSaveTimeoutRef.current = setTimeout(() => {
      persistReorder();
    }, delay);
  }, [persistReorder, delay]);

  useEffect(() => {
    return () => clearTimeout(reorderSaveTimeoutRef.current);
  }, []);

  return { handleRowReorder, handleDrop };
}
